/**
 * Administration data access.
 *
 * The single place that talks to Supabase for `/admin`, like `classroom.ts` and
 * `billing.ts` before it. It holds no rules: every query is a call to a
 * `security definer` function that has already decided the caller is an
 * administrator, and every one of them raises if they are not.
 *
 * That is worth being explicit about. Nothing in this file is a permission
 * check. `isAdmin()` below exists to decide whether to render a page — if it
 * returned `true` for everybody, an impostor would get an admin page full of
 * errors and no data, because the refusal is in `database/admin-schema.sql`.
 *
 * Fails soft, like its neighbours: no network means an empty dashboard with a
 * message, never a broken page.
 */

import { supabaseClient } from './auth.ts';

/* -------------------------------------------------------------------------- */
/* Who is an administrator                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether the signed-in account is an administrator.
 *
 * Answered by the database, not by anything the client holds, so a modified
 * bundle cannot talk itself into `true` — and if it did, it would be shown an
 * empty page.
 */
export async function isAdmin(): Promise<boolean> {
  const client = supabaseClient();
  if (!client) return false;
  try {
    const { data, error } = await client.rpc('is_admin');
    return !error && data === true;
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* The summary                                                                */
/* -------------------------------------------------------------------------- */

export type SubscriptionCount = { tier: string; status: string; count: number };

export type AdminOverview = {
  generatedAt: number;
  accounts: {
    total: number;
    active: number;
    pending: number;
    suspended: number;
    children: number;
    guardians: number;
    awaitingConsent: number;
    newLast7Days: number;
    newLast30Days: number;
  };
  learners: {
    profiles: number;
    activeLast7Days: number;
    activeLast30Days: number;
    totalXp: number;
    medianXp: number;
  };
  subscriptions: SubscriptionCount[];
  classes: { total: number; archived: number; students: number };
};

/** The platform in numbers, or null when it cannot be read. */
export async function fetchOverview(): Promise<AdminOverview | null> {
  const client = supabaseClient();
  if (!client) return null;
  try {
    const { data, error } = await client.rpc('admin_overview');
    if (error || !data) return null;
    return data as AdminOverview;
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* The account list                                                           */
/* -------------------------------------------------------------------------- */

export type AdminAccount = {
  id: string;
  email: string;
  kind: string;
  status: string;
  createdAt: number;
  consentGrantedAt?: number;
  learnerCount: number;
  lastActiveAt?: number;
  tier?: string;
  subscriptionStatus?: string;
  currentPeriodEnd?: number;
};

export type AdminAccountPage = { accounts: AdminAccount[]; total: number };

export const ACCOUNTS_PER_PAGE = 25;

type AccountRow = {
  id: string;
  email: string;
  kind: string;
  status: string;
  created_at: string;
  consent_granted_at: string | null;
  learner_count: number;
  last_active_at: string | null;
  tier: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  total_count: number;
};

/**
 * One page of accounts.
 *
 * Note what comes back: who signed up, what state their account is in, whether
 * they pay, and whether they have been back lately. Not a single answer any
 * learner has given — the function in the database returns a fixed projection
 * and `learner_profiles.data` is not in it. An operator has no more business
 * reading a child's attempts than a teacher does (§14, §29).
 */
export async function fetchAccounts(search: string, page: number): Promise<AdminAccountPage> {
  const client = supabaseClient();
  if (!client) return { accounts: [], total: 0 };

  try {
    const { data, error } = await client.rpc('admin_accounts', {
      search: search.trim() || null,
      page_size: ACCOUNTS_PER_PAGE,
      page,
    });

    if (error || !data) return { accounts: [], total: 0 };
    const rows = data as AccountRow[];

    return {
      // Carried on every row, so paging needs no second query. Zero rows means
      // zero matches, which is a total of nothing.
      total: rows[0] ? Number(rows[0].total_count) : 0,
      accounts: rows.map((row) => ({
        id: String(row.id),
        email: String(row.email),
        kind: String(row.kind),
        status: String(row.status),
        createdAt: Date.parse(row.created_at),
        ...(row.consent_granted_at ? { consentGrantedAt: Date.parse(row.consent_granted_at) } : {}),
        learnerCount: Number(row.learner_count ?? 0),
        ...(row.last_active_at ? { lastActiveAt: Date.parse(row.last_active_at) } : {}),
        ...(row.tier ? { tier: row.tier } : {}),
        ...(row.subscription_status ? { subscriptionStatus: row.subscription_status } : {}),
        ...(row.current_period_end ? { currentPeriodEnd: Date.parse(row.current_period_end) } : {}),
      })),
    };
  } catch {
    return { accounts: [], total: 0 };
  }
}

/* -------------------------------------------------------------------------- */
/* Minting codes                                                              */
/* -------------------------------------------------------------------------- */

export type MintedCodes = {
  codes: string[];
  batch: string;
  couponId: string;
  /** The offer as a sentence, straight from `describeOffer`. */
  offer: string;
  /** True when checkout will still ask the redeemer for a card. */
  needsCard: boolean;
  /** Set when Stripe stopped part-way and the batch is short. */
  warning?: string;
};

export type MintResult = MintedCodes | { error: string };

/**
 * Mints a batch of promotion codes.
 *
 * The offer is sent; the Stripe secret key is not, and never could be — it
 * lives only in the Edge Function's secrets. The function re-validates every
 * field and re-checks that the caller is an administrator before it contacts
 * Stripe, so this call is a request rather than an instruction.
 */
export async function mintPromoCodes(offer: {
  count: number;
  percent: number;
  duration: string;
  tiers: string[];
  maxRedemptions: number;
  expires: string;
  prefix: string;
  note: string;
  firstTimeOnly: boolean;
}): Promise<MintResult> {
  const client = supabaseClient();
  if (!client) return { error: 'Payments are not available on this deployment.' };

  try {
    const { data, error } = await client.functions.invoke('admin-create-promo-codes', { body: offer });
    if (error) {
      return { error: (await readFunctionError(error)) ?? 'Could not create the codes just now.' };
    }
    const result = data as MintedCodes | null;
    if (!result?.codes?.length) return { error: 'No codes came back. Nothing was created.' };
    return result;
  } catch {
    return { error: 'Could not reach the payment service.' };
  }
}

/** Supabase wraps non-2xx responses; the useful sentence is inside. */
async function readFunctionError(error: unknown): Promise<string | undefined> {
  const context = (error as { context?: Response }).context;
  if (!context || typeof context.json !== 'function') return undefined;
  try {
    const body = (await context.json()) as { error?: string };
    return body.error;
  } catch {
    return undefined;
  }
}
